import { Badge, type DataTableColumn } from "@vjal/ui";
import { humanizeStatus } from "../lib/status-format";
import { companyRows } from "./product-organization-data";
import { CompaniesPage } from "./companies/companies-page";
import { CompanyDetailPage } from "./companies/company-detail-page";
import { ProgramsPage } from "./programs/programs-page";
import { ProgramDetailPage } from "./programs/program-detail-page";
import type { ProductScreenContract, TableRow } from "./product-route-contracts";

// ── Companies (Wave 1 — REBUILT as gold-pattern per-screen pages) ──────────
// The thin contract-surface company workspace was replaced by real per-screen
// pages on the gold pattern: a CompaniesPage list (DataTable + filter + role-
// gated create/edit/archive modals) and a CompanyDetailPage record (header +
// decomposed linked-record sections), each routed through useHonestQuery ->
// <HonestState>. The contract factory below preserves the route registration +
// provenance metadata and renders the new pages via `customBody`.


function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused",
  };
}

export function companyWorkspaceScreen(): ProductScreenContract {
  return {
    title: () => "Companies",
    primaryJob:
      "Review every company in scope, search/filter by status, and create/edit/archive only for staff.",
    secondaryJob: "Company admin is read-only on its own company; coach, cohort lead, and participant have no company route.",
    workTitle: "Company directory",
    rows: companyRows,
    columns: companyColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "error", "read-only"],
    apiFamilies: ["company-service", "program-service", "cohort-service"],
    roleDeltas: "Super admin and admin can create/edit/archive companies; company_admin is read-only; other roles are route-denied.",
    extensionSlots: ["company archive impact", "billing handoff", "event-history lifecycle entry"],
    mobilePosture: "Company filter bar, table rows, and lifecycle controls stack with no horizontal overflow.",
    evidenceRequirement: "Playwright must prove staff create/edit/archive, company_admin read-only, denied-role not-found, and mobile no-overflow.",
    surfaceMode: "focused",
    customBody: CompaniesPage
  };
}

export function companyDetailScreen(): ProductScreenContract {
  return {
    ...companyWorkspaceScreen(),
    title: () => "Companies",
    primaryJob:
      "Inspect one company identity, lifecycle status, and linked programs/cohorts before editing or archiving it.",
    workTitle: "Company record",
    ...focusedDefaults(),
    customBody: CompanyDetailPage
  };
}

// ── Programs (Wave 2 — REBUILT as gold-pattern per-screen pages) ───────────
// The thin contract-surface program workspace was replaced by real per-screen
// pages on the gold pattern: a ProgramsPage list (DataTable + filter + role-
// gated create/edit/publish/archive modals) and a ProgramDetailPage record
// (config header + the assigned-cohorts READ section), each routed through
// useHonestQuery -> <HonestState>. The contract factory below preserves the
// route registration + provenance metadata and renders the new pages via
// `customBody`.
//
// CONTRACT CHECK: the backend exposes ONLY programs.list/create/detail/update —
// there is NO program->cohort assignment write endpoint. A cohort carries a
// single programId, so "assignment" is set at cohort create/edit (Wave 3). The
// detail page therefore surfaces the assigned cohorts as a READ and does NOT
// invent an assign flow.
//
// PARTICIPANT is DENIED THE PROGRAMS MODULE: the route manifest's programs /
// program-detail routes use operationsRoles (super_admin/admin/coach/
// company_admin), which excludes participant + cohort_lead — so the route is
// generic-not-found for them and the Programs nav item is absent.

export function programWorkspaceScreen(): ProductScreenContract {
  return {
    title: () => "Programs",
    primaryJob:
      "Review every program in scope, search/filter by status, and create/edit/publish/archive only for staff.",
    secondaryJob:
      "Coach, company admin, and cohort lead see read-only program context; participant has no program route (it appears only in the Participant Dashboard).",
    workTitle: "Program directory",
    rows: programListRows,
    columns: programColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "error", "read-only"],
    apiFamilies: ["program-service", "company-service", "user-service", "cohort-service"],
    roleDeltas:
      "Super admin and admin can create/edit/publish/archive programs; coach and company_admin are read-only; participant and cohort_lead are route-denied.",
    extensionSlots: ["program archive impact", "coach assignment templates", "event-history lifecycle entry"],
    mobilePosture: "Program filter bar, table rows, and lifecycle controls stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove staff create/edit/publish/archive, coach/company_admin read-only, participant route-denied with no Programs nav item, and mobile no-overflow.",
    surfaceMode: "focused",
    customBody: ProgramsPage
  };
}

export function programDetailScreen(): ProductScreenContract {
  return {
    ...programWorkspaceScreen(),
    title: () => "Programs",
    primaryJob:
      "Inspect one program configuration, lifecycle status, company/coach assignment, and assigned cohorts.",
    workTitle: "Program record",
    ...focusedDefaults(),
    customBody: ProgramDetailPage
  };
}

// A representative program row for the route-contract metadata (the live list is
// the real source; this seeds the contract's `rows` so the registry test sees a
// non-empty shape).
const programListRows: TableRow[] = [
  { id: "program-1", name: "Executive Coaching", company_name: "Acme Leadership", coach_name: "Coach One", status: "active" }
];

function companyColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "company", header: "Company" },
    { key: "status", header: "Status", render: (row) => <StatusBadge value={String(row.status)} /> },
    { key: "linkedPrograms", header: "Programs" },
    { key: "linkedCohorts", header: "Cohorts" },
    { key: "lifecycle", header: "Lifecycle" }
  ];
}

function programColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "name", header: "Program" },
    { key: "company_name", header: "Company" },
    { key: "coach_name", header: "Coach" },
    { key: "status", header: "Status", render: (row) => <StatusBadge value={String(row.status)} /> }
  ];
}

function StatusBadge({ value }: { value: string }) {
  return <Badge variant={value === "active" ? "success" : "warning"}>{humanizeStatus(value)}</Badge>;
}
